import type { RefCallback } from 'react';
import { memo, useCallback, useEffect, useState, useRef } from 'react';
import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import { useBlockAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles';
import type {
  IframeBlockStateType,
  IframeComponentType,
} from '@unifyapps/defs/blocks/Iframe/types';
import useBlockEvents from '@unifyapps/carbon/no-code/hooks/useBlockEvents';
import { EmptyState } from '@unifyapps/ui/components/EmptyState';
import SvgAlertCircle from '@unifyapps/icons/outline/AlertCircle';
import { clsx } from 'clsx';
import { useTranslation } from '@unifyapps/i18n/client';
import { Stack } from '@unifyapps/ui/components/Stack';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import { Box } from '@unifyapps/ui/components/Box';
import { InterfaceModes, useInterfaceStore } from '@unifyapps/carbon/no-code/stores/InterfaceStore';
import { useBuilderInterfaceStore } from '@unifyapps/carbon/no-code/stores/InterfaceStore/builder';
import { getSandboxAttributes, getPermissionsPolicy } from './utils';

function IframeOverlay({
  currentBlockId,
  attributesAndEventListeners,
  dndRef,
}: {
  currentBlockId: string;
  attributesAndEventListeners?: Record<string, unknown>;
  dndRef?: RefCallback<HTMLElement | null> | null;
}) {
  const activeBlockId = useBuilderInterfaceStore().use.activeBlockId();

  if (activeBlockId === currentBlockId) {
    return null;
  }

  return (
    <Box className="z-table absolute size-full" {...attributesAndEventListeners} ref={dndRef} />
  );
}

function Iframe({
  computedBlockState,
  dndRef,
  attributesAndEventListeners,
  events,
}: BlockComponentProps<IframeComponentType, IframeBlockStateType, IframeBlockStateType>) {
  const { content, appearance } = computedBlockState;
  const { src, srcDoc, attributes } = content;
  const [isBlocked, setIsBlocked] = useState(false);
  const { t } = useTranslation('blocks');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const { emitEvent } = useBlockEvents(events, computedBlockState.id);
  const pageMode = useInterfaceStore().use.mode();

  const { className, style } = useBlockAppearanceStyles({
    appearanceStyles: appearance?.styles,
  });

  const handleEvent = useCallback(
    (e: SecurityPolicyViolationEvent) => {
      if (!src?.includes(e.blockedURI)) return;
      setIsBlocked(true);
    },
    [src],
  );

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Check if message is from our iframe by comparing source window
      if (iframeRef.current && event.source === iframeRef.current.contentWindow) {
        void emitEvent({ eventType: EventTypeEnum.OnMessageReceived });
      }
    },
    [emitEvent],
  );

  useEffect(() => {
    window.addEventListener('securitypolicyviolation', handleEvent);

    return () => {
      window.removeEventListener('securitypolicyviolation', handleEvent);
    };
  }, [handleEvent, src]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  useEffect(() => {
    setIsBlocked(false);
  }, [src, srcDoc]);

  const onLoad = useCallback(() => {
    setIsBlocked(false);
  }, []);

  if ((!src && !srcDoc) || isBlocked) {
    return (
      <Stack
        {...attributesAndEventListeners}
        alignItems="center"
        className={clsx(className, 'flex-shrink-0')}
        justifyContent="center"
        ref={dndRef}
        style={style}
      >
        <EmptyState
          Icon={SvgAlertCircle}
          description={
            isBlocked
              ? t('blocks:Iframe.ContentBlocked.Description')
              : t('blocks:Iframe.NoContent.Description')
          }
          size="md"
          title={
            isBlocked ? t('blocks:Iframe.ContentBlocked.Title') : t('blocks:Iframe.NoContent.Title')
          }
          variant="default"
        />
      </Stack>
    );
  }

  const permissionsPolicy = getPermissionsPolicy(attributes);
  const sandboxAttributes = getSandboxAttributes(attributes);

  return (
    <Box
      className={clsx(className, 'relative flex-shrink-0 overflow-hidden')}
      {...attributesAndEventListeners}
    >
      {pageMode === InterfaceModes.BUILDER ? (
        <IframeOverlay
          attributesAndEventListeners={attributesAndEventListeners}
          currentBlockId={computedBlockState.id}
          dndRef={dndRef}
        />
      ) : null}
      <iframe
        allow={permissionsPolicy}
        allowFullScreen={attributes?.fullscreen}
        className="size-full"
        key={`iframe-${permissionsPolicy}-${sandboxAttributes}`}
        onLoad={onLoad}
        ref={iframeRef}
        sandbox={sandboxAttributes}
        src={src}
        srcDoc={srcDoc}
        style={style}
        title={src}
      />
    </Box>
  );
}

export default memo(Iframe);
